<template>
  <div class="page-wrapper">
    <div class="generate-title">Generate Monthly Ledgers</div>
    <div class="button-row">
      <button @click="processProperties()">Start</button>
      <button @click="continueProcessing" :disabled="!isWaiting">Next</button>
      <button
        @click="runRemaining"
        :disabled="!isWaiting"
        :class="{ 'active-button': doTheRest }"
      >
        All
      </button>
      <button @click="showPreview">Preview</button>
    </div>
    <div class="ledger-wrapper-outer">
      <div class="ledger-wrapper-inner">
        <!-- Ledger Summary Section -->
        <div id="ledgerBody" v-if="selectedLease">
          <div class="ledger-summary">
            <div
              class="ledger-title"
              style="
                display: flex;
                justify-content: space-between;
                align-items: center;
              "
            >
              <span style="text-align: left"
                >Ledger for {{ selectedProperty.display }}</span
              >
              <span :style="computedStyle">
                {{ balanceMessage }}
              </span>
            </div>

            <!-- first Row -->
            <div class="summary-row full-width">
              <div class="summary-item">
                <label>Tenant(s):</label>
                <strong>{{ ledgerSummary.tenant }}</strong>
              </div>
            </div>
            <!-- second Row -->
            <div class="summary-row">
              <div class="summary-item">
                <strong>{{ ledgerSummary.property }}</strong>
              </div>
              <div class="summary-item">
                <label>from:</label>
                <strong>{{ formatDate(ledgerSummary.startsOn) }}</strong>
              </div>
              <div class="summary-item">
                <label>to:</label>
                <strong>{{ formatDate(ledgerSummary.endsOn) }}</strong>
              </div>
              <div class="summary-item" style="justify-content: right">
                <label>rent:</label>
                <strong>{{ formatRent(ledgerSummary.monthlyBaseRent) }}</strong>
              </div>
            </div>

            <!-- Third Row -->
            <div class="summary-row">
              <div class="summary-item">
                <label>Sec. Deposit:</label>
                <strong>{{ formatRent(ledgerSummary.securityDeposit) }}</strong>
                <label>&nbsp;paid on:</label>
                <strong>{{
                  ledgerSummary.paidOn ? formatDate(ledgerSummary.paidOn) : ''
                }}</strong>
              </div>
              <div class="summary-item">
                <a href="#">{{ ledgerSummary.paymentRef }}</a>
              </div>
              <div class="summary-item" style="justify-content: right">
                <label>prorated:</label>
                <strong>{{ formatRent(ledgerSummary.prorated) }}</strong>
              </div>
            </div>
          </div>

          <!-- Ledger Table Section -->
          <div class="transaction-table-outer">
            <div class="transaction-table-inner">
              <table class="transaction-table">
                <thead>
                  <tr>
                    <th
                      v-for="(header, index) in ledgerHeaders"
                      :key="index"
                      :class="getColumnClass(index)"
                    >
                      {{ header }}
                      <!-- Display totalCredit for colIndex 2 and totalDebit for colIndex 3 -->
                      <div v-if="index === 2">
                        {{ formattedTotalCredit }}
                      </div>
                      <div v-if="index === 3">
                        {{ formattedTotalDebit }}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody v-if="selectedLease.startsOn">
                  <tr
                    v-for="(row, rowIndex) in filteredLedgerData"
                    :key="rowIndex"
                    @click="this.selectedLedgerRow = rowIndex"
                    @dblclick="editLedgerRow(rowIndex)"
                    :class="{
                      'selected-row': rowIndex === selectedLedgerRow,
                    }"
                  >
                    <td
                      v-for="(value, colIndex) in Object.values(row)"
                      :key="colIndex"
                      :class="getColumnClass(colIndex)"
                    >
                      <div v-if="colIndex === 0">
                        {{ formatDate(value) }}
                      </div>
                      <div
                        v-else-if="colIndex === 4"
                        style="text-align: center; width: 120px"
                      >
                        {{ value }}
                      </div>
                      <div v-else-if="colIndex === 5" style="width: 50px">
                        {{ value }}
                      </div>
                      <div
                        v-else-if="
                          colIndex === 2 ||
                          colIndex === 3 ||
                          (colIndex >= 6 && colIndex <= 8)
                        "
                      >
                        {{ formatRent(value) }}
                      </div>

                      <!-- Display note icon with tooltip for colIndex 9 -->
                      <div v-else-if="colIndex === 9">
                        <i
                          v-if="value"
                          class="fas fa-sticky-note"
                          :title="value"
                        ></i>
                      </div>
                      <div
                        v-else-if="colIndex === 10"
                        @click="selectCheck(value)"
                      >
                        <i
                          v-if="value"
                          class="fas fa-money-check"
                          :title="value"
                        ></i>
                      </div>

                      <!-- Default rendering for other values -->
                      <div v-else>{{ value }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="ledger-howto">
                How to read this ledger: Your net balance adjusts on the first
                of each month by deducting your rent due amount; "Expected Rent
                Pmt" rows shows the dates when this occurs. Your payments are
                added to your net balance to bring it toward zero. A negative
                net balance means you owe money; a positive net balance means
                you have a net credit amount.<br /><br />Ideally, each month
                your balance should be zero after your payments are applied. If
                you have unpaid amounts carried forward, or have late or other
                fees assessed, your balance will show as a negative number to
                reflect this. You should pay this remaining balances as soon as
                possible, but no later than with your next rent payment. If you
                have co-tenants, payments will show the initials of the payees
                to help you reconcile your individual payments.
              </div>
              <div class="no-start-date" v-if="!selectedLease.startsOn">
                no valid start date for lease
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div>
    <!-- Preview Modal -->
    <div v-if="showPreviewModal" class="modal">
      <div class="modal-content">
        <button class="close-button" @click="closePreview">X</button>
        <div class="modal-header">
          Email Preview
          <span style="font-weight: normal; font-size: 0.9em"
            >( {{ previewedEmail.id }} )</span
          >
        </div>

        <div class="email-input-group">
          <label for="emailTo">To:</label>
          <input type="text" v-model="previewedEmail.emailTo" readonly />
        </div>
        <div class="email-input-group">
          <label for="emailSubject">Subject:</label>
          <input type="text" v-model="previewedEmail.emailSubject" readonly />
        </div>
        <div class="email-input-group">
          <div class="email-body" v-html="renderedEmailBody"></div>
        </div>

        <div class="button-row">
          <button @click="viewAttachment">Attachment</button>
          <button @click="sendEmail">Send</button>
          <button @click="nextPreview">Next</button>
        </div>
      </div>
    </div>

    <!-- Attachment Viewer -->
    <div v-if="showAttachmentViewer" class="attachment-viewer-underlay">
      <div class="attachment-viewer">
        <div class="attachment-header">
          <span>Attachment Viewer</span>
          <button
            style="position: absolute; right: 50px"
            @click="nextPreview(true)"
          >
            Next
          </button>
          <button
            style="position: absolute; right: 20px"
            @click="closeAttachmentViewer"
          >
            X
          </button>
        </div>
        <iframe :src="attachmentUrl"></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig'; // Adjust based on your axios configuration
import { marked } from 'marked';
import eventBus from './eventBus.js';
import { leaseSendAddresses } from '@/utils/personEmailRows';

export default {
  name: 'AutomateLedgers',
  data() {
    return {
      doTheRest: false,
      selectedLedgerRow: null,
      isWaiting: false,
      resolveContinue: null,
      tenants: [], // To store tenant data fetched from API
      ledgerData: [], // To manage ledger entries
      ledgerSummary: {}, // To store summary information for the ledger
      selectedProperty: {},
      selectedLease: {},
      recalcLedger: false, // Flag to control ledger recalculation process
      ledgerHeaders: [
        'Date',
        'Description',
        'Credits',
        'Debits',
        'Tr# (payer initials)',
        'Type',
        'Running Balance',
        'Expected Balance',
        'Net Balance',
        'Note',
        'Doc',
      ],

      emailTo: '',
      emailSubject: '',
      emailBody: '',
      showPreviewModal: false,
      showAttachmentViewer: false,
      previewedEmail: {
        id: 0,
        emailTo: '',
        emailSubject: '',
        emailBody: '',
      },
      attachmentUrl: '',
      currentPreviewIndex: 0,
      emailPreviews: [], // Stores emails fetched from API
    };
  },

  computed: {
    renderedEmailBody() {
      return marked(this.previewedEmail.emailBody);
    },

    computedStyle() {
      const balance = this.selectedLease?.balance;

      return {
        background:
          typeof balance === 'undefined' || isNaN(balance)
            ? '#ccc' // Default background when balance is undefined or invalid
            : balance == 0
              ? '#aaa'
              : balance < 0
                ? '#955858'
                : 'green',
        color: 'white',
        textAlign: 'right',
        padding: '4px',
      };
    },

    filteredLedgerData() {
      return this.ledgerData.map((row) => {
        const values = Object.values(row);
        return values.filter((_, colIndex) => colIndex <= 10);
      });
    },

    balanceMessage() {
      let result;
      if (this.selectedLease) {
        const balance = this.selectedLease.balance;
        const formattedBalance = Math.abs(balance).toLocaleString(undefined, {
          style: 'currency',
          currency: 'USD',
        });

        switch (true) {
          case balance === 0:
            result = 'Balance: $0.00';
            break;
          case balance < 0:
            result = `You owe: ${formattedBalance}`;
            break;
          default:
            result = `You have a credit of: ${formattedBalance}`;
            break;
        }
      }
      return result;
    },

    totalCredit() {
      return this.filteredLedgerData
        .reduce((sum, row) => {
          const creditValue = parseFloat(row[2]) || 0; // Column 2 for Credit
          return sum + creditValue;
        }, 0)
        .toFixed(2);
    },

    totalDebit() {
      return this.filteredLedgerData
        .reduce((sum, row) => {
          const debitValue = parseFloat(row[3]) || 0; // Column 3 for Debit
          return sum + debitValue;
        }, 0)
        .toFixed(2);
    },

    formattedTotalCredit() {
      return this.formatCurrency(this.totalCredit);
    },

    formattedTotalDebit() {
      return this.formatCurrency(this.totalDebit);
    },
  },

  methods: {
    showPreview() {
      this.showPreviewModal = true;
      this.previewEmail();
    },

    runRemaining() {
      this.doTheRest = true;
      this.continueProcessing();
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },

    async processProperties() {
      try {
        // Fetch all properties
        const properties = await this.fetchProperties();

        // Call a new API /clear-ledgerlist using axios to first delete ledgers
        await axios.post('/api/clear-ledgerlist');

        for (let property of properties) {
          // For each property, fetch the active lease
          const lease = await this.fetchLeases(property.propertyId);
          this.selectedLease = lease;

          if (lease) {
            // If an active lease is found, show ledger
            await this.showLedger(property, lease);

            // Add the lease/ledger to the watch list
            await this.addToList();

            // Wait for button press before continuing
            if (!this.doTheRest) await this.waitForButtonPress();
          }
        }
        this.doTheRest = false;
        console.log('finished all.');
      } catch (error) {
        console.error('Error processing properties:', error);
      }
    },

    waitForButtonPress() {
      this.isWaiting = true;
      return new Promise((resolve) => {
        this.resolveContinue = resolve;
      });
    },

    continueProcessing() {
      this.isWaiting = false;
      if (this.resolveContinue) {
        this.resolveContinue();
        this.resolveContinue = null;
      }
    },

    fetchProperties() {
      // Return the Axios promise
      return axios
        .get('/api/property-lease-summary')
        .then((response) => {
          // Assuming response.data contains the array of properties
          this.propertyData = response.data.map((property) => ({
            ...property,
            balance: 0, // Initially setting balance to 0; this will be updated later
          }));
          return response.data;
        })
        .catch((error) => {
          console.error('Error fetching properties:', error);
          // Handle error appropriately (e.g., show a message to the user)
          throw error; // Rethrow the error to propagate it to the caller
        });
    },

    async fetchLeases(propertyId) {
      // Fetch leases for the selected property
      return axios
        .get(`/api/leases/${propertyId}`)
        .then((response) => {
          const leases = response.data;

          // Get current date at start of day for accurate comparison
          const now = new Date();
          now.setHours(0, 0, 0, 0);

          // Find the active lease
          const activeLease = leases.find((lease) => {
            const startsOn = new Date(lease.startsOn);
            const endsOn = new Date(lease.endsOn);

            // Ensure we compare only dates, not times
            return startsOn <= now && now <= endsOn;
          });

          // Return active lease if found, otherwise null
          return activeLease || null;
        })
        .catch((error) => {
          console.error('Error fetching leases:', error);
          return null; // Return null in case of an error
        });
    },

    async fetchTenants(leaseId) {
      return axios
        .get(`/api/people/${leaseId}`)
        .then((response) => {
          this.tenants = response.data;
        })
        .catch((error) => {
          console.error('Error fetching tenants:', error);
        });
    },

    async fetchLeasePayments_ll(leaseId) {
      try {
        const response = await axios.get(
          `/api/lease-payments?leaseId=${leaseId}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching lease payments:', error);
        throw error;
      }
    },

    async fetchLeaseLedger_ll(leaseId) {
      try {
        const response = await axios.get(
          `/api/lease-ledger?leaseId=${leaseId}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching lease ledger:', error);
        throw error;
      }
    },

    // for ledger
    getColumnClass(colIndex) {
      if (colIndex === 0) return 'left-align';
      if (colIndex === 4) return 'center-align';
      if (colIndex === 1) return 'left-align wide-col';
      if (colIndex === 2 || colIndex === 3 || (colIndex >= 6 && colIndex <= 8))
        return 'right-align';
      return 'center-align';
    },

    async showLedger(property, lease) {
      this.recalcLedger = true;

      if (!property || !lease) {
        this.recalcLedger = false;
        return;
      }

      this.ledgerData = [];

      this.ledgerSummary = {
        property: property.address || '', // assuming property.display is the address or identifier
        tenant: null,
        securityDeposit: lease.securityDeposit ? lease.securityDeposit : '',
        startsOn: lease.startsOn || '',
        endsOn: lease.endsOn || '',
        monthlyBaseRent: lease.monthlyBaseRent || '',
        prorated: lease.prorated || '0.00',
        paidOn: lease.secDepPaidOn || '',
      };

      // let initialBalance = lease.totalLeasePayment - (lease.prorated || 0);
      // 1. Add Opening Balance (fixed runningBalance)
      this.ledgerData.push({
        date: new Date(lease.startsOn),
        description: 'Opening Balance',
        credit: '',
        debit: lease.totalLeasePayment,
        num: '',
        type: '', // No type for opening balance
        runningBalance: lease.totalLeasePayment, // This is fixed for the opening balance
        expectedBalance: lease.totalLeasePayment,
        netBalance: 0,
        note: '',
        docId: '',
      });

      // 2. Fetch required data from the APIs
      const leasePayments = await this.fetchLeasePayments_ll(lease.id);
      const leaseLedger = await this.fetchLeaseLedger_ll(lease.id);

      await this.fetchTenants(lease.id); // Existing API for tenants
      const tenantNames = this.tenants
        .map((tenant) => tenant.fullName)
        .join(', '); // Map and join to get a comma-separated list of names
      this.ledgerSummary.tenant = tenantNames; // Set the tenant field to the formatted string

      const paymentTypeMap = {
        1: 'Check',
        2: 'E-Check',
        3: 'Cashiers',
        4: 'Other',
      };

      // 3. Add Prorated Rent if applicable
      if (lease.prorated !== 0 && lease.prorated !== null) {
        // Find all prorated payments (purpose === 2)
        const proratedPayments = leasePayments.filter(
          (payment) => payment.purpose === 2,
        );

        // Add a ledger entry for each prorated payment
        proratedPayments.forEach((proratedPayment) => {
          const proratedType = proratedPayment
            ? paymentTypeMap[proratedPayment.method]
            : '';

          this.ledgerData.push({
            date: new Date(proratedPayment.paidOn),
            description: 'Prorated Rent pmt',
            credit: proratedPayment.amount,
            debit: '',
            num: proratedPayment ? proratedPayment.transactionNumber : '',
            type: proratedType,
            runningBalance: null, // This will be updated after sorting
            expectedBalance: null, // Will be updated later
            netBalance: null, // To be updated later
            note: proratedPayment ? proratedPayment.note : '',
            docId: proratedPayment ? proratedPayment.docId : '',
            ledgerId: null,
            paymentId: proratedPayments.id,
          });
        });
      }

      // 4. Add Actual Payments and Ledger Entries (excluding prorated payments)
      leasePayments
        .filter((payment) => payment.purpose !== 2) // Exclude prorated payments
        .forEach((payment) => {
          const paymentType = paymentTypeMap[payment.method];

          const amount = payment.amount;
          const effectiveDate = payment.effectiveDate
            ? payment.effectiveDate
            : payment.paidOn;

          let description = 'Payment';
          let credit = amount;
          let debit = '';

          if (amount < 0) {
            description = 'Conv. fee';
            credit = '';
            debit = -amount;
          }

          this.ledgerData.push({
            date: new Date(effectiveDate),
            description: description,
            credit: credit,
            debit: debit,
            num: payment.transactionNumber,
            type: paymentType,
            runningBalance: null, // To be updated later
            expectedBalance: 0, // Compute expected balance for all records except opening balance
            netBalance: null, // To be updated later
            note: payment.note,
            docId: payment.docId,
            ledgerId: null,
            paymentId: payment.id,
          });
        });

      const ledgerTypeMap = {
        0: 'Other',
        1: 'Fee',
        2: 'Credit',
        3: 'Refund',
      };

      leaseLedger.forEach((ledgerEntry) => {
        this.ledgerData.push({
          date: ledgerEntry.date,
          description: ledgerEntry.description,
          credit: ledgerEntry.credit || '',
          debit: ledgerEntry.debit || '',
          num: ledgerEntry.TransactionNum,
          type: ledgerTypeMap[ledgerEntry.type],
          runningBalance: null, // To be updated later
          expectedBalance: lease.totalLeasePayment, // Compute expected balance for all records except opening balance
          netBalance: null,
          note: ledgerEntry.note || '',
          docId: '',
          ledgerId: ledgerEntry.id,
          paymentId: '',
        });
      });

      // 5. Add Expected Rent Payments
      const startsOnDate = new Date(lease.startsOn); // Ensure lease.startsOn is a Date object
      const endMonth = new Date(lease.endsOn); // Convert endsOn to a Date object if it's a string

      // Initialize the currentMonth as the start of the period
      let currentMonth =
        parseFloat(lease.prorated) !== 0 && lease.prorated
          ? new Date(
              startsOnDate.getFullYear(),
              startsOnDate.getMonth(),
              startsOnDate.getDate(),
            ) // Case 1: Start on lease.startsOn date
          : new Date(startsOnDate.getFullYear(), startsOnDate.getMonth(), 1); // Cases 2 and 3: Start on 1st of the month

      // Loop through each month from startMonth to endMonth (inclusive)
      // Initialize monthIndex to 0
      let monthIndex = 0;

      // Loop through each month from startMonth to endMonth (inclusive)
      while (
        currentMonth.getFullYear() < endMonth.getFullYear() ||
        (currentMonth.getFullYear() === endMonth.getFullYear() &&
          currentMonth.getMonth() <= endMonth.getMonth())
      ) {
        const monthStartDate = new Date(currentMonth); // Use the first day of the current month as date

        // Case 1: Prorated payment exists
        if (parseFloat(lease.prorated) !== 0 && lease.prorated) {
          if (monthIndex === 0) {
            // The first payment (prorated)
            this.ledgerData.push({
              date: monthStartDate,
              description: 'Expected Prorated Pmt',
              credit: '',
              debit: '',
              num: '',
              type: '',
              runningBalance: null, // Will be updated later
              expectedBalance: lease.totalLeasePayment - lease.prorated,
              netBalance: null, // To be calculated later
              note: '',
              docId: '',
            });
          } else {
            // Subsequent payments
            this.ledgerData.push({
              date: monthStartDate,
              description: 'Expected Rent Pmt',
              credit: '',
              debit: '',
              num: '',
              type: '',
              runningBalance: null, // Will be updated later
              expectedBalance:
                lease.totalLeasePayment -
                lease.prorated -
                monthIndex * lease.monthlyBaseRent,
              netBalance: null, // To be calculated later
              note: '',
              docId: '',
            });
          }
        }

        // Case 2: No prorated rent, but startsOn is not the first of the month
        else if (
          (lease.prorated === null || parseFloat(lease.prorated) === 0) &&
          startsOnDate.getDate() !== 1
        ) {
          // The first payment starts from the next month
          if (monthIndex > 0) {
            this.ledgerData.push({
              date: monthStartDate,
              description: 'Expected Rent Pmt',
              credit: '',
              debit: '',
              num: '',
              type: '',
              runningBalance: null, // Will be updated later
              expectedBalance:
                lease.totalLeasePayment - monthIndex * lease.monthlyBaseRent,
              netBalance: null, // To be calculated later
              note: '',
              docId: '',
            });
          }
        }
        // Case 3: No prorated rent, and startsOn is the 1st of the month
        else if (
          (lease.prorated === null || parseFloat(lease.prorated) === 0) &&
          startsOnDate.getDate() === 1
        ) {
          // Payments start on the first of the month
          this.ledgerData.push({
            date: monthStartDate,
            description: 'Expected Rent Pmt',
            credit: '',
            debit: '',
            num: '',
            type: '',
            runningBalance: null, // Will be updated later
            expectedBalance:
              lease.totalLeasePayment -
              (monthIndex + 1) * lease.monthlyBaseRent,
            netBalance: null, // To be calculated later
            note: '',
            docId: '',
          });
        }

        // Move to the next month
        currentMonth.setMonth(currentMonth.getMonth() + 1);
        currentMonth.setDate(1);

        // Increment monthIndex
        monthIndex++;
      }

      // 6. Sort by date in ascending order
      this.ledgerData.sort((a, b) => {
        // Extract only the date (ignore time)
        const dateA = new Date(a.date).setHours(0, 0, 0, 0); // Set time to 00:00:00
        const dateB = new Date(b.date).setHours(0, 0, 0, 0); // Set time to 00:00:00

        const dateDiff = dateA - dateB;
        // If dates are the same, prioritize as follows:
        // 1. Opening Balance
        // 2. Expected Rent Pmt
        // 3. Other transactions
        if (dateDiff === 0) {
          if (
            a.description === 'Opening Balance' &&
            b.description !== 'Opening Balance'
          ) {
            return -1; // Sort 'a' before 'b'
          }
          if (
            a.description !== 'Opening Balance' &&
            b.description === 'Opening Balance'
          ) {
            return 1; // Sort 'b' before 'a'
          }
          if (
            a.description === 'Expected Prorate Pmt' &&
            b.description !== 'Expected Prorate Pmt'
          ) {
            return -1; // Sort 'a' before 'b'
          }
          if (
            a.description !== 'Expected Prorate Pmt' &&
            b.description === 'Expected Prorate Pmt'
          ) {
            return 1; // Sort 'b' before 'a'
          }
          if (
            a.description === 'Expected Rent Pmt' &&
            b.description !== 'Expected Rent Pmt'
          ) {
            return -1; // Sort 'a' before 'b'
          }
          if (
            a.description !== 'Expected Rent Pmt' &&
            b.description === 'Expected Rent Pmt'
          ) {
            return 1; // Sort 'b' before 'a'
          }
        }

        // If dates are different, sort by date
        return dateDiff;
      });

      // 7. Calculate runningBalance and other balance fields
      this.ledgerData.forEach((entry, index) => {
        if (index === 0) {
          // The first entry is the opening balance, so we don't update it
          return;
        }

        const previousEntry = this.ledgerData[index - 1];

        // Update runningBalance
        const previousBalance = parseFloat(previousEntry.runningBalance || 0);
        const credit = parseFloat(entry.credit || 0);
        const debit = parseFloat(entry.debit || 0);
        entry.runningBalance = previousBalance + debit - credit;

        // Calculate the monthIndex: months between entry date and lease startsOnDate
        const entryDate = new Date(entry.date);
        const startsOnDate = new Date(lease.startsOn); // Assume lease.startsOn is a valid Date object
        let monthIndex =
          (entryDate.getFullYear() - startsOnDate.getFullYear()) * 12 +
          (entryDate.getMonth() - startsOnDate.getMonth());

        // Adjust for prorated rent: Case 1, where prorated rent is applied
        if (lease.prorated != null && parseFloat(lease.prorated) !== 0) {
          if (monthIndex === 0) {
            // First month with prorated rent
            entry.expectedBalance = lease.totalLeasePayment - lease.prorated;
          } else {
            // Subsequent months
            entry.expectedBalance =
              lease.totalLeasePayment -
              lease.prorated -
              monthIndex * lease.monthlyBaseRent;
          }
        }
        // Case 2: No prorated rent, and lease.startsOn is not the first day of the month
        else if (
          (lease.prorated === null || parseFloat(lease.prorated) === 0) &&
          startsOnDate.getDate() !== 1
        ) {
          // Start balance from the first of the next month
          entry.expectedBalance =
            lease.totalLeasePayment - monthIndex * lease.monthlyBaseRent;
        }
        // Case 3: No prorated rent, and lease starts on the first day of the month
        else if (
          (lease.prorated === null || parseFloat(lease.prorated) === 0) &&
          startsOnDate.getDate() === 1
        ) {
          // Expected balance from the first day of the month
          entry.expectedBalance =
            lease.totalLeasePayment - (monthIndex + 1) * lease.monthlyBaseRent;
        }

        // Determine if netBalance should be meaningful or blank
        const currentMonth = new Date();
        if (entryDate <= currentMonth) {
          // Calculate netBalance only for months up to and including the current month
          entry.netBalance = entry.expectedBalance - entry.runningBalance;
          this.selectedLease.balance = entry.netBalance;
          //
        } else {
          // Set netBalance as blank for future months
          entry.netBalance = '';
        }
      });

      // update the balance for the selected lease
      const { id, balance } = this.selectedLease;
      axios
        .put('/api/update-lease-balance/', { id, balance })
        .then(() => {})
        .catch((error) => {
          console.error(
            'Error updating balance:',
            error.response ? error.response.data.error : error.message,
          );
          // Handle error appropriately, maybe show a message to the user
        });
      this.recalcLedger = false;
    },

    inlineScopedCSS(element) {
      // inject styles
      return (
        `
      <style>
          /* Base styles */
          body { padding: 10px; font-family: Consolas; box-sizing: border-box; }
          #ledgerBody { margin-top: 5px; font-size: 0.9em; margin-bottom: 20px; }
          
          /* Ledger summary styling with table-based layout for better PDF rendering */
          #ledgerBody .ledger-summary { 
            background-color: #f2f2f2; 
            padding: 10px; 
            border-bottom: 2px solid #000; 
            font-size: 0.9em; 
            width: calc(100% - 5px) !important; /* Subtract padding to match table width */
            box-sizing: border-box !important;
            margin: 0 auto !important;
          }
          
          /* Title section with absolute positioning for better PDF rendering */
          #ledgerBody .ledger-title { 
            position: relative;
            font-size: 1.3em; 
            font-weight: bold; 
            margin-top: 5px; 
            margin-bottom: 10px;
            width: 100%;
            height: 30px;
            clear: both;
          }
          
          #ledgerBody .ledger-title > span:first-child {
            position: absolute;
            left: 0;
            top: 0;
            text-align: left;
          }
          
          #ledgerBody .ledger-title > span:last-child {
            position: absolute;
            right: 0;
            top: 0;
            background-color: #aaa;
            color: white;
            text-align: right;
            padding: 4px 8px;
            border-radius: 3px;
          }
          
          /* Explicit balance indicator styles for PDF rendering */
          .balance-indicator {
            position: absolute !important;
            right: 0 !important;
            top: 0 !important;
            background-color: #aaa !important;
            color: white !important;
            text-align: right !important;
            padding: 4px 8px !important;
            border-radius: 3px !important;
            display: inline-block !important;
            margin: 0 !important;
          }
          
          /* Summary rows with table-like structure for better PDF compatibility */
          #ledgerBody .summary-row { 
            display: table !important;
            table-layout: fixed !important;
            width: 100% !important;
            margin-bottom: 5px !important;
          }
          
          #ledgerBody .summary-row.full-width { 
            width: 100% !important; 
          }
          
          #ledgerBody .summary-item {
            display: table-cell !important;
            vertical-align: middle !important;
            padding: 2px !important;
          }
          
          #ledgerBody .summary-item:first-child { 
            width: 40% !important; 
          }
          
          #ledgerBody .summary-item[style*="justify-content: right"] {
            text-align: right !important;
          }
          
          #ledgerBody .summary-item label { 
            font-weight: normal !important;
            margin-right: 5px !important;
            display: inline-block !important;
          }
          
          #ledgerBody .summary-item strong, 
          #ledgerBody .summary-item a { 
            white-space: nowrap !important;
            display: inline-block !important;
          }
          
          /* Table styling - unchanged but with !important for PDF generation */
          #ledgerBody .transaction-table { 
            width: 100% !important; 
            border-collapse: collapse !important; 
            margin-top: 10px !important; 
            font-size: 11px !important; 
            box-sizing: border-box !important;
          }
          #ledgerBody .transaction-table th, #ledgerBody .transaction-table td { border: 1px solid #aaa !important; padding: 5px !important; }
          #ledgerBody .transaction-table th { background-color: #bbbbbb !important; text-align: left !important; vertical-align: top !important; }
          #ledgerBody .transaction-table td.left-align, #ledgerBody .transaction-table th.left-align { text-align: left !important; }
          #ledgerBody .transaction-table td.right-align, #ledgerBody .transaction-table th.right-align { text-align: right !important; }
          #ledgerBody .transaction-table td.center-align, #ledgerBody .transaction-table th.center-align { text-align: center !important; }
          #ledgerBody .transaction-table tbody tr:nth-child(even) { background-color: #ccc !important; }
          #ledgerBody .transaction-table tbody tr:nth-child(odd) { background-color: #ffffff !important; }
          #ledgerBody .transaction-table tbody tr.highlight td { font-style: italic !important; color: red !important; }
          #ledgerBody .footer-summary { text-align: right !important; font-weight: bold !important; }
          .no-start-date { margin-top: 20px !important; }
          .selected-row { background-color: #907793 !important; color: #fff !important; }
          .ledger-howto { 
            margin-top: 10px !important; 
            font-size: 0.75em !important; 
            font-style: italic !important; 
            padding: 5px !important; 
            background: white !important; 
            color: #111 !important; 
            page-break-inside: avoid !important; 
            width: 100% !important;
            box-sizing: border-box !important;
          }
          
          /* Wrapper to align all content */
          .transaction-table-outer, .transaction-table-inner {
            width: 100% !important;
            box-sizing: border-box !important;
          }
          </style>
  ` + element
      );
    },

    async printLedger(elementName, mode = 'email') {
      this.selectedLedgerRow = null;
      try {
        const element = document.getElementById(elementName);
        if (!element) {
          console.error('Element not found:', elementName);
          return null; // Return null if element is not found
        }

        // Clone the element to avoid modifying the original HTML
        const clonedElement = element.cloneNode(true);

        // Ensure the balance indicator is correctly positioned and styled
        const balanceElement = clonedElement.querySelector(
          '.ledger-title > span:last-child',
        );
        if (balanceElement) {
          // Apply position-based styling and explicit class for PDF rendering
          balanceElement.setAttribute(
            'style',
            'position: absolute; right: 0; top: 0; background-color: #aaa; color: white; text-align: right; padding: 4px 8px; border-radius: 3px;',
          );
          balanceElement.classList.add('balance-indicator');

          // Set the balance background color based on value
          if (this.selectedLease.balance < 0) {
            balanceElement.style.backgroundColor = '#955858';
          } else if (this.selectedLease.balance > 0) {
            balanceElement.style.backgroundColor = 'green';
          } else {
            balanceElement.style.backgroundColor = '#aaa';
          }
        }

        // Ensure the title container has position: relative for correct element positioning
        const titleElement = clonedElement.querySelector('.ledger-title');
        if (titleElement) {
          titleElement.style.position = 'relative';
          titleElement.style.width = '100%';
          titleElement.style.height = '30px';
          titleElement.style.marginBottom = '20px';
        }

        // Convert flex-based summary rows to table-based layout for better PDF rendering
        const summaryRows = clonedElement.querySelectorAll('.summary-row');
        summaryRows.forEach((row) => {
          row.style.display = 'table';
          row.style.tableLayout = 'fixed';
          row.style.width = '100%';

          const items = row.querySelectorAll('.summary-item');
          items.forEach((item) => {
            item.style.display = 'table-cell';
            item.style.verticalAlign = 'middle';

            // Handle right-aligned items
            if (
              item.getAttribute('style') &&
              item.getAttribute('style').includes('justify-content: right')
            ) {
              item.style.textAlign = 'right';
            }
          });
        });

        // Apply fixed width to the ledger summary
        const summaryElement = clonedElement.querySelector('.ledger-summary');
        if (summaryElement) {
          summaryElement.style.width = 'calc(100% - 20px)';
          summaryElement.style.boxSizing = 'border-box';
          summaryElement.style.margin = '0 auto';
        }

        // Modify table cells for visibility in PDF
        clonedElement.querySelectorAll('tr').forEach((row) => {
          const cells = row.querySelectorAll('td, th');
          if (cells.length >= 2) {
            cells[1].style.width = '130px';
            cells[4].style.width = '110px';
            cells[5].style.width = '70px';
          }
          if (cells.length >= 2) {
            cells[cells.length - 1].style.display = 'none';
            cells[cells.length - 2].style.display = 'none';
            cells[cells.length - 4].style.display = 'none';
            cells[cells.length - 5].style.display = 'none';
          }
        });

        // Apply consistent box-sizing to ensure width calculations are consistent
        const outerElement = clonedElement.querySelector(
          '.transaction-table-outer',
        );
        if (outerElement) {
          outerElement.style.width = '100%';
          outerElement.style.boxSizing = 'border-box';
        }

        const innerElement = clonedElement.querySelector(
          '.transaction-table-inner',
        );
        if (innerElement) {
          innerElement.style.width = '100%';
          innerElement.style.boxSizing = 'border-box';
        }

        const htmlContentWithCSS = this.inlineScopedCSS(
          clonedElement.outerHTML,
        );
        console.log('Using PDF conversion with html-pdf');

        // Use the convert-to-pdf endpoint
        const response = await axios.post(
          '/api/convert-to-pdf',
          { html: htmlContentWithCSS },
          { responseType: 'blob' },
        );

        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });

        if (mode === 'print') {
          const url = URL.createObjectURL(pdfBlob);
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = url;
          document.body.appendChild(iframe);
          iframe.onload = () => {
            if (iframe.contentWindow) {
              iframe.contentWindow.print();
              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                  URL.revokeObjectURL(url);
                  this.showFooterMessage(
                    'Removed print preview after 10 seconds.',
                  );
                }
              }, 20000); // Adjust delay as needed
            }
          };
        } else if (mode === 'email') {
          return pdfBlob; // Return the Blob for email attachment
        }
      } catch (error) {
        console.error('Error generating PDF:', error);
        this.showFooterMessage('Failed to generate PDF - please try again');
        return null;
      }
    },

    setEmailSubject() {
      const date = new Date();

      // Array of month names
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];

      // Get the current month name and year
      const monthAndYear = `${monthNames[date.getMonth()]}, ${date.getFullYear()}`;

      // Construct the email subject
      return `Your ${monthAndYear} ledger from Pixie Properties is attached`;
    },

    // Add the currently viewed lease/ledger to the watch list
    async addToList() {
      try {
        // Call printLedger and capture the generated Blob
        const docuBlob = await this.printLedger('ledgerBody', 'email'); // Ensure printLedger returns a Blob

        // Prepare tenant information
        const tenantNames = this.selectedLease.peopleEmails.map(
          (person) => person.fullName,
        );

        // Create docTitle from property and current date
        const currentDate = new Date()
          .toISOString()
          .replace('T', ' ')
          .slice(0, 16);
        const docTitle = `${this.ledgerSummary.property}-${currentDate}`;

        const tenantEmails = leaseSendAddresses(this.tenants).join(', ');

        let emailBodyTop = '';

        if (this.selectedLease.balance < 0) {
          emailBodyTop = `Dear ${tenantNames},<br><br>\nPlease see your attached ledger, and advise us how you plan to bring your account balance to zero.<br><br>`;
        } else if (this.selectedLease.balance > 0) {
          emailBodyTop = `Dear ${tenantNames},<br><br>\nPlease see your attached ledger. Because you have a credit balance, you can reduce your next month's payment by this amount, if you wish.<br><br>`;
        } else {
          emailBodyTop = `Dear ${tenantNames},<br><br>\nPlease see your attached ledger. Thank you for keeping your account current with us.<br><br>`;
        }
        const emailBodyBottom =
          '<br><br>Regards,<br>**The Pixie Properties Team**<br>616-916-6953';
        const emailBody = emailBodyTop + emailBodyBottom;

        // Prepare FormData to handle the merged Blob
        const formData = new FormData();

        formData.append('docTitle', docTitle);
        formData.append('docuBlob', docuBlob); // Append combined Blob
        formData.append('leaseId', this.selectedLease.id || 0);
        formData.append('type', 2);
        formData.append('emailTo', tenantEmails);
        formData.append('emailBody', emailBody);
        formData.append('emailSubject', this.setEmailSubject());

        // Send to add-ledgerlist API
        await axios.post('/api/add-ledgerlist', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        // Refresh the ledger list after adding the new entry
        // await this.fetchLedgerList();
      } catch (error) {
        console.error('Failed to add to ledger list:', error);
      } finally {
        this.isAdding = false;
      }
    },

    formatDate(dateInput) {
      // If the input is a string, directly parse it; if it's a Date object, handle it as is
      if (!dateInput) return '[--/--/--]';

      const date =
        typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

      if (isNaN(date.getTime())) {
        // Return a default value if the date is invalid
        return 'Invalid Date';
      }

      // Extract the local date components without any timezone adjustments
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth is 0-indexed, so add 1
      const day = String(date.getDate()).padStart(2, '0');

      // Return the formatted date as MM/DD/YYYY
      const formatted = `${month}/${day}/${year}`;
      // console.log(dateInput, formatted);
      return formatted;
    },

    formatDateTime(dateString) {
      const options = {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'EST',
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },

    formatRent(rent) {
      const rentNumber = parseFloat(rent);
      if (isNaN(rentNumber)) {
        return rent; // Return the original value if it's not a valid number
      }
      return rentNumber.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    isToday(date) {
      const today = new Date().toISOString().split('T')[0];
      return today === date.split('T')[0];
    },

    async previewEmail() {
      try {
        const response = await axios.get('/api/fetch-email-previews');
        this.emailPreviews = response.data;
        if (this.emailPreviews.length > 0) {
          this.currentPreviewIndex = 0;
          this.loadCurrentPreview();
          this.showPreviewModal = true;
        }
      } catch (error) {
        console.error('Error fetching email previews:', error);
      }
    },

    loadCurrentPreview() {
      const currentEmail = this.emailPreviews[this.currentPreviewIndex];
      this.previewedEmail = {
        id: currentEmail.id,
        emailTo: currentEmail.emailTo,
        emailSubject: currentEmail.emailSubject,
        emailBody: currentEmail.emailBody,
      };
    },

    showFooterMessage(message) {
      // Set the footer message
      eventBus.emit('footerMessage', message);
    },

    async sendEmail() {
      try {
        await axios.post('/api/send-email', {
          from: 'you@example.com',
          subject: this.previewedEmail.emailSubject,
          body: this.previewedEmail.emailBody,
          recipients: this.previewedEmail.emailTo,
        });
        this.showFooterMessage('Email sent successfully');
      } catch (error) {
        console.error('Failed to send email:', error);
      }
    },

    nextPreview(showAttach = false) {
      if (this.currentPreviewIndex < this.emailPreviews.length - 1) {
        this.currentPreviewIndex++;
        this.loadCurrentPreview();

        if (showAttach) {
          this.viewAttachment();
        }
      } else {
        this.showFooterMessage('No more previews available');
      }
    },

    closePreview() {
      this.showPreviewModal = false;
    },

    async viewAttachment() {
      try {
        // Check if there are any email previews and if the current index is valid
        if (
          !this.emailPreviews ||
          this.emailPreviews.length === 0 ||
          this.currentPreviewIndex < 0 ||
          this.currentPreviewIndex >= this.emailPreviews.length
        ) {
          this.showFooterMessage('No email attachments available to view');
          return;
        }

        const currentEmailId = this.emailPreviews[this.currentPreviewIndex].id;

        const response = await axios.get(
          `/api/fetch-ledgerlist-attachment?id=${currentEmailId}`,
          {
            responseType: 'arraybuffer', // Ensures we get the data in binary form
          },
        );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        this.attachmentUrl = URL.createObjectURL(blob);
        this.showAttachmentViewer = true;
      } catch (error) {
        console.error('Error fetching attachment:', error);
        this.showFooterMessage(
          'Failed to load attachment: ' + (error.message || 'Unknown error'),
        );
      }
    },

    closeAttachmentViewer() {
      this.showAttachmentViewer = false;
      URL.revokeObjectURL(this.attachmentUrl);
    },
  },
  mounted() {
    document.title = 'Automate Ledgers';
  },
};
</script>
<style scoped>
/* Ledger body styles */
#ledgerBody {
  margin-top: 5px;
  font-size: 0.9em;
  margin-bottom: 20px;
}

#ledgerBody .ledger-summary {
  background-color: #f2f2f2;
  padding: 10px;
  border-bottom: 2px solid #000;
  font-size: 0.9em;
}

#ledgerBody .ledger-title {
  font-size: 1.3em;
  font-weight: bold;
  margin-top: 5px;
}

#ledgerBody .summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

#ledgerBody .summary-item:first-child {
  flex: 2;
}

#ledgerBody .summary-item {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

#ledgerBody .summary-item label {
  font-weight: normal;
  margin-right: 5px;
}

#ledgerBody .summary-item strong,
#ledgerBody .summary-item a {
  white-space: nowrap;
}

#ledgerBody .transaction-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 11px;
}

#ledgerBody .transaction-table th,
#ledgerBody .transaction-table td {
  border: 1px solid #aaa;
  padding: 5px;
}

#ledgerBody .transaction-table th {
  background-color: #bbbbbb;
  text-align: left;
  vertical-align: top;
}

#ledgerBody .transaction-table td.left-align,
#ledgerBody .transaction-table th.left-align {
  text-align: left;
}

#ledgerBody .transaction-table td.right-align,
#ledgerBody .transaction-table th.right-align {
  text-align: right;
}

#ledgerBody .transaction-table td.center-align,
#ledgerBody .transaction-table th.center-align {
  text-align: center;
}

#ledgerBody .transaction-table tbody tr:nth-child(even) {
  background-color: #ccc;
}

#ledgerBody .transaction-table tbody tr:nth-child(odd) {
  background-color: #ffffff;
}

#ledgerBody .transaction-table tbody tr.highlight td {
  font-style: italic;
  color: red;
}

#ledgerBody .footer-summary {
  text-align: right;
  font-weight: bold;
}

.ledger-howto {
  margin-top: 10px;
  font-size: 0.8em;
  color: #666;
}

.no-start-date {
  margin-top: 20px;
  color: #d9534f;
  font-weight: bold;
}

.button-row {
  width: 100%;
  text-align: center;
  padding: 8px;
}

.page-wrapper {
  width: 864px;
  margin: 15px;
  margin-top: 5px;
  padding: 8px;
  border: 1pt solid #ddd;
  height: 813px;
  overflow-y: hidden;
  overflow-x: hidden;
  border-radius: 4px;
}
.ledger-wrapper-outer {
  width: 858px;
  overflow: hidden;
}
.ledger-wrapper-inner {
  width: 873px;
  height: 735px;
  overflow-y: scroll;
  overflow-x: hidden;
}

.button-row button {
  width: 120px;
  margin-left: 8px;
  border-radius: 4px;
  border: 1pt solid #ddd;
  padding: 8px;
  background: #a080a8;
  color: white;
  cursor: pointer;
}

.button-row button:disabled {
  background: #d3d3d3;
  color: #808080;
  border-color: #c0c0c0;
  cursor: not-allowed;
  opacity: 0.6;
}

.generate-title {
  font-size: 1.4em;
  margin-bottom: 8px;
  width: 100%;
  text-align: center;
}

.subpanel-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.email-form label {
  font-weight: bold;
  margin-bottom: 5px;
}

.email-form input[type='text'],
.email-form textarea {
  width: 100%;
  padding: 10px;
  font-size: 1em;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.email-input-group input {
  padding: 5px;
  border: 1pt solid #ddd;
  border-radius: 5px;
  position: absolute;
  width: 424px;
  right: 17px;
}
.email-input-group label {
  font-size: 0.9em;
  font-weight: bold;
}

.email-body {
  border-radius: 5px;
  border: 1pt solid #ddd;
  padding: 5px;
  width: 491px;
  margin-top: 5px;
  height: 262px;
  overflow-y: auto;
  font-size: 0.9em;
}

.email-form input[type='checkbox'] {
  margin-left: 10px;
}

.email-form textarea {
  height: 120px;
  resize: vertical;
}

.modal {
  position: fixed;
  top: 50%;
  left: 300px;
  transform: translate(0, -50%);
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  width: 500px;
  max-width: 90%;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.modal-header {
  font-size: 1.2em;
  font-weight: bold;
  text-align: center;
  margin: -20px;
  margin-bottom: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  background: #333;
  color: #eee;
  font-size: 1.2em;
  padding: 8px;
}

.close-button {
  position: absolute;
  top: 8px;
  right: 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1em;
  color: #eee;
}

.attachment-viewer-underlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}

.attachment-viewer {
  width: 800px;
  height: 638px;
  position: absolute;
  left: 200px;
  top: 20%;
  box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.3);
}

.attachment-header {
  font-size: 1.2em;
  font-weight: bold;
  text-align: center;
  margin-bottom: 0px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  background: #333;
  color: #eee;
  font-size: 1.2em;
  padding: 8px;
}

iframe {
  width: 800px;
  height: 600px;
  border: none;
}

.active-button {
  border: 1pt solid yellow !important;
}
</style>
